import { useMutation, useQuery } from '@apollo/client';
import { Button, Flex, Grid, IconButton, Spinner, Stack, Text } from '@chakra-ui/react';
import { RouteComponentProps } from '@reach/router';
import get from 'lodash/get';
import { useContext, useReducer } from 'react';
import { SquaresFour, List as ListIcon } from '@phosphor-icons/react';

import Layout from '../../../components/Layout';
import SpinnerContext from '../../../contexts/SpinnerContext';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { AccessPointCard } from './Card';
import { AccessPointsQuery, EXPORT_UPS_ACCESS_POINTS_CSV, GET_ACCESS_POINTS } from './query';
import { AccessPointTableRow } from './Row';

interface AccessPointsListProps extends RouteComponentProps {}

const initialState = { status: 'ALL', view: 'card' };

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_STATUS':
      return { ...state, status: action.payload };
    case 'SET_VIEW':
      return { ...state, view: action.payload };
  }
};

const localStatusToGraphqlStatus = (status: string) => {
  switch (status) {
    case 'ALL':
      return ['PENDING', 'WAITLIST', 'CONFIRMED', 'ACTIVE', 'INACTIVE', 'REJECTED', 'RETRY'];
    case 'DRAFT':
      return ['DRAFT'];
    case 'PENDING':
      return ['PENDING'];
    case 'WAITLIST':
      return ['WAITLIST'];
    case 'CONFIRMED':
      return ['CONFIRMED', 'ACTIVE', 'INACTIVE'];
    case 'REJECTED':
      return ['REJECTED', 'RETRY'];
  }
};

export const AccessPointsList: React.FC<AccessPointsListProps> = () => {
  const { toggleSpinner } = useContext(SpinnerContext);
  const toast = useCustomToast();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { data, loading, error, refetch, fetchMore } = useQuery<AccessPointsQuery>(GET_ACCESS_POINTS, {
    variables: {
      status: localStatusToGraphqlStatus(state.status),
    },
  });
  const [exportUPSAccessPointsCSV] = useMutation(EXPORT_UPS_ACCESS_POINTS_CSV);

  const handleExportUPSAccessPointsCSV = async () => {
    try {
      toggleSpinner(true);
      const { data } = await exportUPSAccessPointsCSV();
      window.open(data.exportUPSAccessPointsCSV, '_blank');
    } catch (error) {
      console.log(error);

      toast({
        description: get(error, 'graphQLErrors.0.message', 'Ein Fehler ist aufgetreten!'),
        status: 'error',
      });
    }
    toggleSpinner(false);
  };

  const switchUserView = (type: 'card' | 'table') => {
    localStorage.setItem('userView', type);

    dispatch({ type: 'SET_VIEW', payload: type });
  };

  const AccessPointComponent = state.view === 'card' ? AccessPointCard : AccessPointTableRow;

  console.log(data);

  return (
    <Layout>
      <Flex flexDirection={['column', 'row']} alignItems="center" justifyContent="space-between" mb="6">
        <Text as="h2" fontSize={['2xl', '4xl']} fontWeight="bold" flex="1" fontFamily="heading">
          Access Points
        </Text>
        <Button colorScheme="brand" size="sm" onClick={handleExportUPSAccessPointsCSV}>
          Prüfungs-CSV herunterladen
        </Button>
      </Flex>

      <Flex justifyContent="space-between" alignItems="flex-start" mb="4" flexWrap="wrap" gap="3">
        <Stack>
          {/* <FilterValues dispatch={dispatch} state={state} /> */}
          <Text fontSize="sm" gridColumn="span 3">
            Angezeigte AccessPoints: {data?.accessPoints?.length}
          </Text>
        </Stack>

        <Stack direction={['column', 'row']} flexWrap="wrap" justifyContent="flex-end" flex="1">
          <Flex flexWrap="wrap" justifyContent="flex-end" gap="2">
            <Button
              size="sm"
              onClick={() => {
                dispatch({
                  type: 'SET_STATUS',
                  payload: 'ALL',
                });
              }}
              colorScheme={state.status === 'ALL' ? 'blue' : undefined}
            >
              Alle
            </Button>
            <Button
              size="sm"
              onClick={() => {
                dispatch({
                  type: 'SET_STATUS',
                  payload: 'DRAFT',
                });
              }}
              colorScheme={state.status === 'DRAFT' ? 'blue' : undefined}
            >
              Draft
            </Button>
            <Button
              size="sm"
              onClick={() => {
                dispatch({
                  type: 'SET_STATUS',
                  payload: 'PENDING',
                });
              }}
              colorScheme={state.status === 'PENDING' ? 'blue' : undefined}
            >
              Pending
            </Button>
            <Button
              size="sm"
              onClick={() => {
                dispatch({
                  type: 'SET_STATUS',
                  payload: 'WAITLIST',
                });
              }}
              colorScheme={state.status === 'WAITLIST' ? 'blue' : undefined}
            >
              Auf Warteliste
            </Button>
            <Button
              size="sm"
              onClick={() => {
                dispatch({
                  type: 'SET_STATUS',
                  payload: 'CONFIRMED',
                });
              }}
              colorScheme={state.status === 'CONFIRMED' ? 'green' : undefined}
            >
              Genehmigt
            </Button>
            <Button
              size="sm"
              onClick={() => {
                dispatch({
                  type: 'SET_STATUS',
                  payload: 'REJECTED',
                });
              }}
              colorScheme={state.status === 'REJECTED' ? 'red' : undefined}
            >
              Nicht Freigegeben
            </Button>
          </Flex>
          <Flex flexWrap="wrap" justifyContent="flex-end" gap="2">
            {/* <Flex>
              <Select
                size="sm"
                w="260px"
                rounded="md"
                onChange={(event) => {
                  console.log(event.target.value);
                  dispatch({ type: 'SET_ORDER_BY', orderBy: event.target.value });
                }}
                value={state.orderBy}>
                <option value="createdAt_DESC">Registriert am (Abw)</option>
                <option value="createdAt_ASC">Registriert am (Aufw)</option>
                <option value="activityCheck_DESC">Auf Aktivitätscheck reagiert</option>
                <option value="activityCheck_ASC">Nicht reagiert auf Aktivitätscheck</option>
              </Select>
            </Flex>
            <UserSearch dispatch={dispatch} state={state} /> */}
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={state.view === 'card' ? 'Switch to Table' : 'Switch to Card'}
              icon={state.view === 'card' ? <ListIcon /> : <SquaresFour />}
              onClick={() => {
                switchUserView(state.view === 'card' ? 'table' : 'card');
              }}
            />
          </Flex>
        </Stack>
      </Flex>

      {error ? (
        <Flex width="100%" height="100%" justifyContent="center" alignItems="center">
          Ein Fehler ist aufgetreten. Schreib Osamah an.
        </Flex>
      ) : null}
      {data && data.accessPoints ? (
        <>
          <Grid gridTemplateColumns={['1fr', '1fr 1fr', '1fr 1fr', '1fr 1fr']} gap={state.view === 'card' ? '16px' : 0} mb="8">
            {data.accessPoints.length === 0 ? (
              <Text gridColumn="span 3" p="4" textAlign="center" backgroundColor="red.100" color="red.800" borderRadius="4px">
                Der Widerstand versteckt sich gut und konnte nicht gefunden werden. Wiederhole die Suche oder schicke Boba Fett!
              </Text>
            ) : (
              <>
                {data.accessPoints.map((accessPoint) => {
                  if (!accessPoint) return null;
                  return <AccessPointComponent key={accessPoint.id} {...accessPoint} />;
                })}
              </>
            )}
          </Grid>
          {/* {data.accessPoints.length !== 0 && data.accessPoints.length < data.totalDropPoints2.total && !loading ? (
            <Flex width="100%" justifyContent="center" mb="8">
              <Button
                onClick={() =>
                  fetchMore({
                    variables: {
                      skip: data.packageStations.length,
                    },
                    updateQuery: (prev: any, { fetchMoreResult }) => {
                      if (!fetchMoreResult) return prev;
                      return Object.assign({}, prev, {
                        packageStations: [...prev.packageStations, ...fetchMoreResult.packageStations],
                      });
                    },
                  })
                }>
                Mehr laden
              </Button>
            </Flex>
          ) : null} */}
        </>
      ) : null}

      {loading ? (
        <Flex width="100%" height="100%" justifyContent="center" alignItems="center">
          <Spinner />
        </Flex>
      ) : null}
    </Layout>
  );
};
